import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowRight,
  ArrowsClockwise,
  ChartLineUp,
  CheckCircle,
  CircleNotch,
  FlowArrow,
  Lightning,
  Megaphone,
  Plus,
  RocketLaunch,
  Trash,
  SealCheck,
  WarningCircle,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function normalizePlan(plan) {
  return String(plan || "starter").trim().toLowerCase();
}

function isProLike(plan) {
  return ["pro", "company", "agency", "enterprise"].includes(normalizePlan(plan));
}

function readItems(data) {
  if (Array.isArray(data)) return data;
  for (const key of ["items", "data", "results", "projects", "workflows", "approvals", "tasks"]) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

function safeStatus(value, fallback = "unknown") {
  return String(value || fallback).trim().toLowerCase();
}

function projectStatus(project) {
  return safeStatus(project?.status || "active", "active");
}

function isArchivedProject(project) {
  return ["archived", "deleted"].includes(projectStatus(project));
}

function workflowStatus(workflow) {
  const status = safeStatus(workflow?.status || "");
  if (["paused", "inactive", "disabled"].includes(status)) return "paused";
  if (["active", "enabled"].includes(status)) return "active";
  if (
    workflow?.active === false ||
    workflow?.is_active === false ||
    workflow?.is_enabled === false ||
    workflow?.is_enabled === 0 ||
    workflow?.is_enabled === "0"
  ) {
    return "paused";
  }
  return "active";
}

function tryJson(value) {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function itemProjectId(item) {
  const payload = typeof item?.input_payload === "string" ? tryJson(item.input_payload) : item?.input_payload || {};
  const metadata = typeof item?.metadata === "string" ? tryJson(item.metadata) : item?.metadata || {};
  return String(
    item?.project_id ||
      item?.projectId ||
      payload?.project_id ||
      payload?._project?.project_id ||
      metadata?.project_id ||
      metadata?.project?.project_id ||
      ""
  ).trim();
}

function itemProjectName(item) {
  const payload = typeof item?.input_payload === "string" ? tryJson(item.input_payload) : item?.input_payload || {};
  const metadata = typeof item?.metadata === "string" ? tryJson(item.metadata) : item?.metadata || {};
  return String(
    item?.project_name ||
      item?.projectName ||
      payload?.project_name ||
      payload?._project?.name ||
      metadata?.project_name ||
      metadata?.project?.name ||
      ""
  ).trim();
}

function percent(value, max) {
  if (!max || Number(max) <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((Number(value || 0) / Number(max)) * 100)));
}

function statusPillClass(status) {
  const value = safeStatus(status);
  if (["active", "published", "completed", "ok", "healthy"].includes(value)) {
    return "bg-[#DCFCE7] border-[#BBF7D0] text-[#166534]";
  }
  if (["ready", "scheduled", "pending", "running"].includes(value)) {
    return "bg-[#EFF6FF] border-[#BFDBFE] text-[#1D4ED8]";
  }
  if (["paused", "archived", "failed", "warning", "risk"].includes(value)) {
    return "bg-[#FEF2F2] border-[#FECACA] text-[#B91C1C]";
  }
  return "bg-[#F5F5F5] border-[#E5E7EB] text-[#525252]";
}

function MiniPill({ children, status = "default" }) {
  return (
    <span className={`inline-flex border px-2 py-1 text-[10px] font-mono uppercase tracking-[0.16em] ${statusPillClass(status)}`}>
      {children}
    </span>
  );
}

function GaugeCard({ label, value, max, note }) {
  const pct = Math.max(0, Math.min(100, percent(value, max)));
  return (
    <div className="border border-[#E5E7EB] bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">{label}</p>
          <p className="mt-1 font-display text-3xl font-black">{value}{max ? `/${max}` : ""}</p>
          <p className="mt-2 text-sm text-[#525252]">{note}</p>
        </div>
        <div
          className="h-24 w-24 rounded-full border border-[#E5E7EB] flex items-center justify-center text-sm font-black"
          style={{
            background: `conic-gradient(#002FA7 ${pct}%, #E5E7EB ${pct}% 100%)`,
          }}
        >
          <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center">{pct}%</div>
        </div>
      </div>
    </div>
  );
}

function FlowNode({ icon: Icon, label, value, note, status = "default", delay = 0 }) {
  return (
    <div
      className="relative border border-[#E5E7EB] bg-white p-4 min-h-[122px] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
      style={{ animation: `ordeneliMapIn 420ms ease ${delay}ms both` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="h-10 w-10 bg-[#EFF6FF] text-[#002FA7] flex items-center justify-center">
          <Icon size={22} weight="duotone" />
        </div>
        <MiniPill status={status}>{status}</MiniPill>
      </div>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">{label}</p>
      <p className="font-display text-3xl font-black mt-1">{value}</p>
      <p className="mt-2 text-sm text-[#525252] leading-relaxed">{note}</p>
    </div>
  );
}

function Connector() {
  return (
    <div className="hidden lg:flex items-center justify-center">
      <div className="flex items-center text-[#002FA7] opacity-80">
        <span className="h-px w-8 bg-[#BFDBFE]" />
        <ArrowRight size={18} weight="bold" />
      </div>
    </div>
  );
}

function VisualModeButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 border text-sm font-semibold transition ${active ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-[#E5E7EB] text-[#111] hover:border-[#002FA7] hover:text-[#002FA7]"}`}
    >
      {children}
    </button>
  );
}

function EmptyVisual({ title, text, actionText, to }) {
  return (
    <div className="border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-8 text-center">
      <FlowArrow size={34} weight="duotone" className="mx-auto text-[#002FA7]" />
      <h3 className="font-display text-2xl font-black mt-4">{title}</h3>
      <p className="mt-2 text-sm text-[#525252] max-w-xl mx-auto leading-relaxed">{text}</p>
      {to && <Link to={to} className="btn-primary inline-flex mt-5">{actionText || "Open"}</Link>}
    </div>
  );
}

function WorkspaceMap({ totals }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-6 md:p-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Workspace activity map</p>
          <h2 className="font-display text-3xl font-black mt-1">From project to published output.</h2>
          <p className="mt-2 text-sm text-[#525252] max-w-2xl">
            A client-friendly view of how your workspace moves from strategy, to approvals,
            to scheduled and published marketing actions.
          </p>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Live from workflow data</span>
      </div>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
        <FlowNode icon={FlowArrow} label="Active projects" value={totals.activeProjects} note={`${totals.archivedProjects} archived projects stay visible for history.`} status={totals.activeProjects > 0 ? "active" : "warning"} delay={0} />
        <Connector />
        <FlowNode icon={RocketLaunch} label="Active workflows" value={totals.activeWorkflows} note="Automations that create drafts, tasks and scheduled output." status={totals.activeWorkflows > 0 ? "active" : "warning"} delay={90} />
        <Connector />
        <FlowNode icon={CheckCircle} label="Approvals" value={totals.pendingApprovals} note="Drafts waiting for review before becoming action tasks." status={totals.pendingApprovals > 0 ? "pending" : "healthy"} delay={180} />
        <Connector />
        <FlowNode icon={Megaphone} label="Published" value={totals.publishedTasks} note={`${totals.scheduledTasks} scheduled · ${totals.readyTasks} ready`} status={totals.publishedTasks > 0 ? "published" : "default"} delay={270} />
      </div>
    </div>
  );
}

function FunnelCanvas({ totals }) {
  const stages = [
    {
      title: "Project base",
      count: totals.activeProjects,
      note: "Active workspaces driving campaigns and outputs.",
      width: "100%",
      bg: "bg-[#F8FAFC]",
    },
    {
      title: "Workflow engine",
      count: totals.activeWorkflows,
      note: "Automations converting strategy into repeatable production.",
      width: "86%",
      bg: "bg-[#EFF6FF]",
    },
    {
      title: "Action queue",
      count: totals.readyTasks + totals.scheduledTasks,
      note: `${totals.readyTasks} ready · ${totals.scheduledTasks} scheduled`,
      width: "72%",
      bg: "bg-[#E0E7FF]",
    },
    {
      title: "Published output",
      count: totals.publishedTasks,
      note: "Outputs that reached WordPress, LinkedIn or final completion.",
      width: "58%",
      bg: "bg-[#DCFCE7]",
    },
  ];

  return (
    <div className="border border-[#E5E7EB] bg-white p-6 md:p-8">
      <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Campaign funnel</p>
      <h2 className="font-display text-3xl font-black mt-1">Show activity like a strategy funnel.</h2>
      <p className="mt-2 text-sm text-[#525252] max-w-3xl">Not every view needs the same diagram. This version turns your operational flow into a conversion-style funnel for reports, proposals and client walkthroughs.</p>

      <div className="mt-8 space-y-4">
        {stages.map((stage, index) => (
          <div key={stage.title} className="flex justify-center">
            <div className={`border border-[#E5E7EB] ${stage.bg} p-5 transition-all hover:shadow-md`} style={{ width: stage.width }}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Stage {index + 1}</p>
                  <h3 className="font-display text-2xl font-black mt-1">{stage.title}</h3>
                  <p className="mt-2 text-sm text-[#525252]">{stage.note}</p>
                </div>
                <div className="text-left md:text-right">
                  <p className="font-display text-4xl font-black">{stage.count}</p>
                  <MiniPill status={index === stages.length - 1 ? "published" : "active"}>{index === stages.length - 1 ? "output" : "live"}</MiniPill>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function JourneyCanvas({ totals }) {
  const steps = [
    { name: "Plan", detail: `${totals.activeProjects} active projects`, icon: FlowArrow, status: "active" },
    { name: "Create", detail: `${totals.activeWorkflows} active workflows`, icon: Lightning, status: "active" },
    { name: "Review", detail: `${totals.pendingApprovals} pending approvals`, icon: CheckCircle, status: totals.pendingApprovals ? "pending" : "healthy" },
    { name: "Schedule", detail: `${totals.scheduledTasks} scheduled tasks`, icon: RocketLaunch, status: totals.scheduledTasks ? "scheduled" : "healthy" },
    { name: "Publish", detail: `${totals.publishedTasks} published outputs`, icon: Megaphone, status: totals.publishedTasks ? "published" : "healthy" },
  ];

  return (
    <div className="border border-[#E5E7EB] bg-white p-6 md:p-8">
      <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Customer / delivery journey</p>
      <h2 className="font-display text-3xl font-black mt-1">Use a journey-style visual, not just a pipeline.</h2>
      <p className="mt-2 text-sm text-[#525252] max-w-3xl">This layout works better when you want something that feels closer to a client story: from planning and creation, to review, scheduling and final publishing.</p>

      <div className="mt-8 grid grid-cols-1 xl:grid-cols-5 gap-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.name} className="relative border border-[#E5E7EB] bg-[#FAFAFA] p-5 overflow-hidden">
              <div className="flex items-center justify-between gap-3">
                <div className="h-11 w-11 bg-white border border-[#E5E7EB] text-[#002FA7] flex items-center justify-center">
                  <Icon size={22} weight="duotone" />
                </div>
                <MiniPill status={step.status}>{step.status}</MiniPill>
              </div>
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Step {index + 1}</p>
              <h3 className="font-display text-2xl font-black mt-1">{step.name}</h3>
              <p className="mt-2 text-sm text-[#525252]">{step.detail}</p>
              {index < steps.length - 1 && (
                <div className="hidden xl:flex absolute top-10 -right-3 items-center text-[#002FA7]">
                  <ArrowRight size={18} weight="bold" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SnapshotCanvas({ totals, usage }) {
  const projectLimit = usage?.projectLimit || usage?.projectsAllowed || 10;
  const seatLimit = usage?.seatLimit || usage?.seatsAllowed || 3;
  const activeKeys = usage?.apiKeysUsed || usage?.apiKeysActive || 0;
  const keyLimit = usage?.apiKeyLimit || usage?.apiKeysAllowed || 3;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <GaugeCard label="Projects" value={totals.activeProjects} max={projectLimit} note="How many live projects are currently using capacity." />
        <GaugeCard label="Team seats" value={usage?.seatsUsed || 0} max={seatLimit} note="Workspace membership and seat usage." />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-6">
        <div className="border border-[#E5E7EB] bg-white p-6 md:p-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Operational comparison</p>
          <h2 className="font-display text-3xl font-black mt-1">A more chart-like business snapshot.</h2>
          <p className="mt-2 text-sm text-[#525252] max-w-2xl">This is closer to a proposal/report visual. It mixes bars, labels and summary notes instead of using only one diagram style.</p>

          <div className="mt-8 space-y-5">
            {[
              { label: "Ready", value: totals.readyTasks, color: "#002FA7" },
              { label: "Scheduled", value: totals.scheduledTasks, color: "#4F46E5" },
              { label: "Published", value: totals.publishedTasks, color: "#16A34A" },
              { label: "Failed", value: totals.failedTasks, color: "#DC2626" },
            ].map((item) => {
              const max = Math.max(1, totals.readyTasks, totals.scheduledTasks, totals.publishedTasks, totals.failedTasks);
              const width = `${Math.max(8, percent(item.value, max))}%`;
              return (
                <div key={item.label}>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-semibold">{item.label}</span>
                    <span className="font-black">{item.value}</span>
                  </div>
                  <div className="mt-2 h-4 bg-[#F5F5F5] border border-[#E5E7EB] overflow-hidden">
                    <div className="h-full transition-all duration-500" style={{ width, backgroundColor: item.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-[#E5E7EB] bg-[#0A0A0A] text-white p-6 md:p-8">
          <ChartLineUp size={30} weight="duotone" className="text-[#8AB4FF]" />
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.22em] text-[#8AB4FF]">Why this matters</p>
          <h3 className="font-display text-2xl font-black mt-2">Different views for different conversations.</h3>
          <div className="mt-5 space-y-4 text-sm text-white/75 leading-relaxed">
            <p>Some clients respond better to funnels. Some respond better to journeys. Some want a chart-like snapshot.</p>
            <p>That is why Strategy Maps should support multiple visual formats instead of showing one identical diagram everywhere.</p>
            <p>Next phase: connect this to saved project inputs and let Ordeneli generate the best visual automatically.</p>
          </div>

          <div className="mt-6 border border-white/10 p-4 bg-white/5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/60">API capacity</p>
            <p className="font-display text-4xl font-black mt-1">{activeKeys}/{keyLimit}</p>
            <p className="mt-2 text-sm text-white/70">Active API keys connected to this workspace.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectCards({ projectCards }) {
  return (
    <div className="grid gap-5">
      {projectCards.map(({ project, workflows, approvals, tasks }, index) => {
        const archived = isArchivedProject(project);
        const activeWorkflows = workflows.filter((item) => workflowStatus(item) === "active").length;
        const pendingApprovals = approvals.filter((item) => safeStatus(item?.status) === "pending").length;
        const readyTasks = tasks.filter((item) => safeStatus(item?.status) === "ready").length;
        const scheduledTasks = tasks.filter((item) => safeStatus(item?.status) === "scheduled").length;
        const publishedTasks = tasks.filter((item) => safeStatus(item?.status) === "published").length;

        if (index % 2 === 0) {
          return (
            <div key={project?.project_id || project?.name} className="border border-[#E5E7EB] bg-white overflow-hidden">
              <div className="p-5 border-b border-[#E5E7EB] flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-2xl font-black">{project?.name || "Untitled project"}</h3>
                    <MiniPill status={archived ? "archived" : "active"}>{archived ? "Archived" : "Active"}</MiniPill>
                  </div>
                  <p className="mt-1 text-sm text-[#525252]">{project?.domain || "No domain added"}{project?.target_location ? ` · ${project.target_location}` : ""}</p>
                </div>
                <Link to="/workflow" className="btn-secondary inline-flex justify-center">Open workflow</Link>
              </div>
              <div className="p-5 grid grid-cols-1 lg:grid-cols-4 gap-3">
                <FlowNode icon={RocketLaunch} label="Workflows" value={activeWorkflows} note="Active automations connected to this project." status={activeWorkflows ? "active" : "warning"} />
                <FlowNode icon={CheckCircle} label="Approvals" value={pendingApprovals} note="Waiting for review and decision." status={pendingApprovals ? "pending" : "healthy"} />
                <FlowNode icon={Lightning} label="Action queue" value={readyTasks + scheduledTasks} note={`${readyTasks} ready · ${scheduledTasks} scheduled`} status={readyTasks + scheduledTasks ? "ready" : "healthy"} />
                <FlowNode icon={Megaphone} label="Published" value={publishedTasks} note="Completed outputs for this project." status={publishedTasks ? "published" : "default"} />
              </div>
            </div>
          );
        }

        return (
          <div key={project?.project_id || project?.name} className="border border-[#E5E7EB] bg-[#FAFAFA] p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-2xl font-black">{project?.name || "Untitled project"}</h3>
                  <MiniPill status={archived ? "archived" : "active"}>{archived ? "Archived" : "Active"}</MiniPill>
                </div>
                <p className="mt-1 text-sm text-[#525252]">Journey-style project map with quick status summary.</p>
              </div>
              <Link to="/workflow" className="btn-secondary inline-flex justify-center">Open workflow</Link>
            </div>
            <div className="mt-6 grid grid-cols-1 xl:grid-cols-5 gap-4">
              {[
                { name: "Project", detail: project?.domain || "Base setup", icon: FlowArrow, status: archived ? "archived" : "active" },
                { name: "Create", detail: `${activeWorkflows} workflows`, icon: RocketLaunch, status: activeWorkflows ? "active" : "warning" },
                { name: "Review", detail: `${pendingApprovals} approvals`, icon: CheckCircle, status: pendingApprovals ? "pending" : "healthy" },
                { name: "Queue", detail: `${readyTasks + scheduledTasks} tasks`, icon: Lightning, status: readyTasks + scheduledTasks ? "ready" : "healthy" },
                { name: "Publish", detail: `${publishedTasks} outputs`, icon: Megaphone, status: publishedTasks ? "published" : "default" },
              ].map((step, stepIndex, arr) => {
                const Icon = step.icon;
                return (
                  <div key={step.name} className="relative border border-[#E5E7EB] bg-white p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="h-10 w-10 bg-[#EFF6FF] text-[#002FA7] flex items-center justify-center">
                        <Icon size={20} weight="duotone" />
                      </div>
                      <MiniPill status={step.status}>{step.status}</MiniPill>
                    </div>
                    <h4 className="font-display text-xl font-black mt-4">{step.name}</h4>
                    <p className="mt-2 text-sm text-[#525252]">{step.detail}</p>
                    {stepIndex < arr.length - 1 && (
                      <div className="hidden xl:flex absolute top-10 -right-3 items-center text-[#002FA7]">
                        <ArrowRight size={18} weight="bold" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FutureDiagramPanel() {
  return (
    <div className="border border-[#BFDBFE] bg-[#EFF6FF] p-6 md:p-8">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Next phase</p>
          <h3 className="font-display text-2xl sm:text-3xl font-black mt-1">Generate editable strategy diagrams from real project inputs.</h3>
          <p className="mt-3 text-sm text-[#1E40AF] max-w-3xl leading-relaxed">
            This page now supports multiple visual styles instead of one repeated layout. Later, Ordeneli can generate the best-fitting diagram for project strategy, campaign flows, customer journeys, proposal visuals and exports.
          </p>
        </div>
        <div className="border border-[#93C5FD] bg-white p-4 min-w-[260px]">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Future outputs</p>
          <div className="mt-4 space-y-3">
            {["Project strategy map", "Campaign funnel", "Customer journey", "Proposal visual"].map((item, index) => (
              <div key={item} className="flex items-center gap-3">
                <span className="h-7 w-7 bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold">{index + 1}</span>
                <span className="text-sm font-semibold">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const DIAGRAM_TYPE_OPTIONS = [
  { value: "campaign_funnel", label: "Campaign funnel" },
  { value: "customer_journey", label: "Customer journey" },
  { value: "workflow_map", label: "Workflow map" },
  { value: "proposal_visual", label: "Proposal visual" },
  { value: "content_strategy", label: "Content strategy map" },
];

function diagramTypeLabel(value) {
  return DIAGRAM_TYPE_OPTIONS.find((item) => item.value === value)?.label || "Strategy diagram";
}

function normalizeGeneratedDiagram(raw) {
  if (!raw) return null;

  const data = raw.diagram_json || raw.diagram || raw;
  const diagram = typeof data === "string" ? tryJson(data) : data;

  if (!diagram || typeof diagram !== "object") return null;

  return {
    diagram_id: raw.diagram_id || diagram.diagram_id || raw.id || "",
    project_id: raw.project_id || diagram.project_id || "",
    title: raw.title || diagram.title || "Strategy diagram",
    type: raw.diagram_type || diagram.type || "workflow_map",
    summary: diagram.summary || raw.summary || "",
    nodes: Array.isArray(diagram.nodes) ? diagram.nodes : [],
    edges: Array.isArray(diagram.edges) ? diagram.edges : [],
    insights: Array.isArray(diagram.insights) ? diagram.insights : [],
    created_at: raw.created_at || diagram.created_at || null,
  };
}

function nodeStyle(index, type) {
  const styles = [
    "bg-[#EFF6FF] border-[#BFDBFE]",
    "bg-[#F8FAFC] border-[#CBD5E1]",
    "bg-[#ECFDF5] border-[#BBF7D0]",
    "bg-[#EEF2FF] border-[#C7D2FE]",
    "bg-[#FFFBEB] border-[#FDE68A]",
    "bg-[#FAF5FF] border-[#E9D5FF]",
  ];

  if (type === "proposal_visual") {
    return index % 2 === 0 ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E5E7EB]";
  }

  return styles[index % styles.length];
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function slugify(value) {
  return String(value || "strategy-diagram")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80) || "strategy-diagram";
}

function diagramPlainText(normalized) {
  if (!normalized) return "";

  const lines = [];
  lines.push(normalized.title || "Strategy diagram");
  lines.push("");
  lines.push(`Type: ${diagramTypeLabel(normalized.type)}`);

  if (normalized.created_at) {
    lines.push(`Generated: ${new Date(normalized.created_at).toLocaleString()}`);
  }

  if (normalized.summary) {
    lines.push("");
    lines.push("Summary");
    lines.push(normalized.summary);
  }

  if (normalized.nodes?.length) {
    lines.push("");
    lines.push("Diagram");
    normalized.nodes.forEach((node, index) => {
      lines.push(`${index + 1}. ${node.label || `Node ${index + 1}`}`);
      if (node.description || node.note) lines.push(`   ${node.description || node.note}`);
      if (node.metric) lines.push(`   Metric: ${node.metric}`);
    });
  }

  if (normalized.insights?.length) {
    lines.push("");
    lines.push("AI notes");
    normalized.insights.forEach((insight) => lines.push(`- ${String(insight)}`));
  }

  lines.push("");
  lines.push("Generated by Ordeneli");
  return lines.join("\n");
}

function buildPrintableDiagramHtml(normalized) {
  const logoUrl = "https://ordeneli.com/ordeneli-email-logo-transparent-180px.png";
  const title = escapeHtml(normalized.title || "Strategy diagram");
  const typeLabel = escapeHtml(diagramTypeLabel(normalized.type));
  const summary = escapeHtml(normalized.summary || "");
  const generatedAt = normalized.created_at ? escapeHtml(new Date(normalized.created_at).toLocaleString()) : escapeHtml(new Date().toLocaleString());
  const nodes = Array.isArray(normalized.nodes) ? normalized.nodes : [];
  const insights = Array.isArray(normalized.insights) ? normalized.insights : [];

  const nodeCard = (node, index, extraStyle = "") => `
    <div class="node-card" style="${extraStyle}">
      <div class="node-kicker">Step ${index + 1}</div>
      <div class="node-title">${escapeHtml(node.label || `Node ${index + 1}`)}</div>
      <div class="node-text">${escapeHtml(node.description || node.note || "")}</div>
      ${node.metric ? `<div class="node-metric">${escapeHtml(node.metric)}</div>` : ""}
    </div>
  `;

  let diagramHtml = "";

  if (normalized.type === "campaign_funnel") {
    diagramHtml = `<div class="funnel">${nodes.map((node, index) => {
      const width = Math.max(54, 100 - index * 10);
      return `<div class="funnel-row"><div class="funnel-card" style="width:${width}%">${nodeCard(node, index)}</div></div>`;
    }).join("")}</div>`;
  } else if (normalized.type === "customer_journey") {
    diagramHtml = `<div class="journey">${nodes.map((node, index) => `
      <div class="journey-item">${nodeCard(node, index)}${index < nodes.length - 1 ? '<div class="arrow">→</div>' : ""}</div>
    `).join("")}</div>`;
  } else if (normalized.type === "proposal_visual") {
    diagramHtml = `<div class="proposal-grid">${nodes.map((node, index) => nodeCard(node, index, index % 2 === 0 ? "background:#0A0A0A;color:#fff;border-color:#0A0A0A;" : "")).join("")}</div>`;
  } else {
    diagramHtml = `<div class="pipeline">${nodes.map((node, index) => `
      <div class="pipeline-item">${nodeCard(node, index)}${index < nodes.length - 1 ? '<div class="pipe-arrow">→</div>' : ""}</div>
    `).join("")}</div>`;
  }

  const insightsHtml = insights.length ? `
    <section class="notes">
      <div class="section-label">AI notes</div>
      <ul>${insights.slice(0, 8).map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>
    </section>` : "";

  const gridCount = Math.min(Math.max(nodes.length, 1), 5);
  const pipeCount = Math.min(Math.max(nodes.length, 1), 4);

  return `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>
    *{box-sizing:border-box}body{margin:0;background:#f5f5f5;color:#111;font-family:Arial,Helvetica,sans-serif}.page{max-width:1080px;margin:0 auto;background:#fff;min-height:100vh;padding:42px}.header{display:flex;align-items:flex-start;justify-content:space-between;gap:28px;border-bottom:1px solid #e5e7eb;padding-bottom:24px;margin-bottom:28px}.logo{width:180px;height:auto;display:block}.kicker,.section-label{font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:#002FA7;font-weight:700;margin-bottom:8px}h1{font-size:34px;line-height:1.05;letter-spacing:-.04em;margin:0}.meta{margin-top:14px;font-size:12px;color:#666;line-height:1.6}.summary{border:1px solid #e5e7eb;background:#f8fafc;padding:18px;margin-bottom:24px;font-size:14px;line-height:1.6;color:#333}.node-card{border:1px solid #e5e7eb;background:#fff;padding:18px;min-height:132px}.node-kicker{font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#737373;font-weight:700}.node-title{font-size:22px;font-weight:900;letter-spacing:-.03em;margin-top:8px;line-height:1.1}.node-text{font-size:13px;line-height:1.55;color:inherit;opacity:.82;margin-top:10px}.node-metric{margin-top:12px;font-size:26px;font-weight:900;color:#002FA7}.funnel{display:grid;gap:12px;margin-top:18px}.funnel-row{display:flex;justify-content:center}.funnel-card>.node-card{background:#eff6ff;border-color:#bfdbfe}.journey{display:grid;grid-template-columns:repeat(${gridCount},1fr);gap:14px;align-items:stretch;margin-top:18px}.journey-item,.pipeline-item{position:relative}.arrow,.pipe-arrow{position:absolute;right:-13px;top:48px;color:#002FA7;font-weight:900;font-size:20px;z-index:2}.proposal-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-top:18px}.pipeline{display:grid;grid-template-columns:repeat(${pipeCount},1fr);gap:14px;margin-top:18px}.pipeline .node-card{background:#f8fafc}.notes{border:1px solid #e5e7eb;background:#f8fafc;padding:18px;margin-top:28px}.notes ul{margin:10px 0 0;padding-left:20px}.notes li{margin:8px 0;font-size:13px;line-height:1.5;color:#333}.footer{border-top:1px solid #e5e7eb;margin-top:34px;padding-top:18px;display:flex;align-items:center;justify-content:space-between;gap:18px;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#737373}.print-hint{margin:18px auto;max-width:1080px;background:#002FA7;color:#fff;padding:12px 18px;font-size:13px;font-weight:700}@media print{body{background:#fff}.page{padding:24px;max-width:none}.print-hint{display:none}.node-card,.summary,.notes,.header{break-inside:avoid}@page{margin:12mm}}@media(max-width:900px){.journey,.pipeline,.proposal-grid{grid-template-columns:1fr}.arrow,.pipe-arrow{display:none}.header{flex-direction:column}}
  </style></head><body><div class="print-hint">Use your browser print dialog and choose “Save as PDF”.</div><main class="page"><header class="header"><div><div class="kicker">Ordeneli strategy diagram</div><h1>${title}</h1><div class="meta">Type: ${typeLabel}<br>Generated: ${generatedAt}</div></div><img class="logo" src="${logoUrl}" alt="Ordeneli"></header>${summary ? `<section class="summary">${summary}</section>` : ""}<section><div class="section-label">Visual map</div>${diagramHtml || `<div class="summary">No diagram nodes were available for this visual.</div>`}</section>${insightsHtml}<footer class="footer"><span>Generated by Ordeneli</span><span>ordeneli.com</span></footer></main><script>window.addEventListener("load",function(){setTimeout(function(){window.focus();window.print();},500);});</script></body></html>`;
}

function exportDiagramPdf(diagram) {
  const normalized = normalizeGeneratedDiagram(diagram);
  if (!normalized) return;

  const html = buildPrintableDiagramHtml(normalized);
  const popup = window.open("", "_blank", "width=1100,height=850");

  if (!popup) {
    toast.error("Popup blocked. Allow popups, then try Export PDF again.");
    return;
  }

  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  toast.success("PDF export opened. Choose Save as PDF in the print dialog.");
}

async function copyDiagramText(diagram) {
  const normalized = normalizeGeneratedDiagram(diagram);
  if (!normalized) return;
  const text = diagramPlainText(normalized);

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    toast.success("Diagram text copied.");
  } catch {
    toast.error("Could not copy diagram text.");
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function wrapCanvasText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 4) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = word;
    }
    if (lines.length >= maxLines) break;
  }

  if (current && lines.length < maxLines) lines.push(current);

  lines.forEach((line, index) => {
    ctx.fillText(line, x, y + index * lineHeight);
  });

  return y + lines.length * lineHeight;
}

function drawRoundedRect(ctx, x, y, width, height, radius = 18) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function drawNodeCard(ctx, node, index, x, y, width, height, dark = false) {
  ctx.save();
  ctx.shadowColor = "rgba(15, 23, 42, 0.10)";
  ctx.shadowBlur = 18;
  ctx.shadowOffsetY = 8;
  drawRoundedRect(ctx, x, y, width, height, 18);
  ctx.fillStyle = dark ? "#0A0A0A" : "#FFFFFF";
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.lineWidth = 2;
  ctx.strokeStyle = dark ? "#0A0A0A" : "#E5E7EB";
  ctx.stroke();

  ctx.fillStyle = dark ? "#93C5FD" : "#002FA7";
  ctx.font = "700 22px Arial";
  ctx.fillText(`STEP ${index + 1}`, x + 28, y + 42);

  ctx.fillStyle = dark ? "#FFFFFF" : "#111111";
  ctx.font = "900 34px Arial";
  wrapCanvasText(ctx, node.label || `Node ${index + 1}`, x + 28, y + 88, width - 56, 38, 2);

  ctx.fillStyle = dark ? "rgba(255,255,255,0.78)" : "#525252";
  ctx.font = "400 22px Arial";
  wrapCanvasText(ctx, node.description || node.note || "", x + 28, y + 168, width - 56, 30, node.metric ? 3 : 4);

  if (node.metric) {
    ctx.fillStyle = dark ? "#93C5FD" : "#002FA7";
    ctx.font = "900 34px Arial";
    ctx.fillText(String(node.metric), x + 28, y + height - 28);
  }
  ctx.restore();
}

function exportDiagramJpeg(diagram) {
  const normalized = normalizeGeneratedDiagram(diagram);
  if (!normalized) return;

  const nodes = normalized.nodes?.length ? normalized.nodes : [];
  const width = 1600;
  const cardHeight = 286;
  const gap = 38;
  let visualHeight = 560;

  if (normalized.type === "campaign_funnel") {
    visualHeight = nodes.length * (cardHeight + gap) + 40;
  } else if (normalized.type === "proposal_visual") {
    visualHeight = Math.ceil(nodes.length / 2) * (cardHeight + gap) + 40;
  } else if (normalized.type === "customer_journey") {
    visualHeight = Math.ceil(nodes.length / 3) * (cardHeight + gap) + 50;
  } else {
    visualHeight = Math.ceil(nodes.length / 3) * (cardHeight + gap) + 50;
  }

  const insightsHeight = normalized.insights?.length ? 230 : 90;
  const height = Math.max(1120, 330 + visualHeight + insightsHeight + 150);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#F5F5F5";
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = "#FFFFFF";
  drawRoundedRect(ctx, 70, 56, width - 140, height - 112, 28);
  ctx.fill();

  ctx.fillStyle = "#0A0A0A";
  ctx.font = "900 42px Arial";
  ctx.fillText("ORDENELI.", width - 350, 132);
  ctx.fillStyle = "#002FA7";
  ctx.fillRect(width - 118, 102, 10, 10);

  ctx.fillStyle = "#002FA7";
  ctx.font = "700 20px Arial";
  ctx.fillText("ORDENELI STRATEGY DIAGRAM", 120, 122);

  ctx.fillStyle = "#111111";
  ctx.font = "900 54px Arial";
  wrapCanvasText(ctx, normalized.title || "Strategy diagram", 120, 190, 1050, 60, 2);

  ctx.fillStyle = "#525252";
  ctx.font = "400 23px Arial";
  const typeLabel = diagramTypeLabel(normalized.type);
  ctx.fillText(`${typeLabel} · Generated ${new Date(normalized.created_at || Date.now()).toLocaleDateString()}`, 120, 270);

  let y = 330;
  if (normalized.summary) {
    ctx.fillStyle = "#F8FAFC";
    drawRoundedRect(ctx, 120, y, width - 240, 105, 18);
    ctx.fill();
    ctx.strokeStyle = "#E5E7EB";
    ctx.stroke();
    ctx.fillStyle = "#334155";
    ctx.font = "400 24px Arial";
    wrapCanvasText(ctx, normalized.summary, 148, y + 40, width - 296, 32, 2);
    y += 145;
  }

  if (normalized.type === "campaign_funnel") {
    nodes.forEach((node, index) => {
      const cardWidth = Math.max(820, width - 280 - index * 86);
      const x = (width - cardWidth) / 2;
      drawNodeCard(ctx, node, index, x, y, cardWidth, cardHeight, false);
      y += cardHeight + gap;
    });
  } else if (normalized.type === "proposal_visual") {
    const colWidth = (width - 280 - gap) / 2;
    nodes.forEach((node, index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = 120 + col * (colWidth + gap);
      const nodeY = y + row * (cardHeight + gap);
      drawNodeCard(ctx, node, index, x, nodeY, colWidth, cardHeight, index % 2 === 0);
    });
    y += Math.ceil(nodes.length / 2) * (cardHeight + gap) + 20;
  } else {
    const cols = Math.min(nodes.length || 1, normalized.type === "customer_journey" ? 3 : 3);
    const cardWidth = (width - 240 - gap * (cols - 1)) / cols;
    nodes.forEach((node, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = 120 + col * (cardWidth + gap);
      const nodeY = y + row * (cardHeight + gap);
      drawNodeCard(ctx, node, index, x, nodeY, cardWidth, cardHeight, false);
      if (index < nodes.length - 1 && col < cols - 1) {
        ctx.fillStyle = "#002FA7";
        ctx.font = "900 42px Arial";
        ctx.fillText("→", x + cardWidth + 7, nodeY + 128);
      }
    });
    y += Math.ceil(nodes.length / cols) * (cardHeight + gap) + 20;
  }

  if (normalized.insights?.length) {
    const visibleInsights = normalized.insights.slice(0, 5);
    const notesBoxHeight = 92 + visibleInsights.length * 36;
    ctx.fillStyle = "#EFF6FF";
    drawRoundedRect(ctx, 120, y + 20, width - 240, notesBoxHeight, 18);
    ctx.fill();
    ctx.strokeStyle = "#BFDBFE";
    ctx.stroke();
    ctx.fillStyle = "#002FA7";
    ctx.font = "700 20px Arial";
    ctx.fillText("AI NOTES", 148, y + 65);
    ctx.fillStyle = "#1E3A8A";
    ctx.font = "400 22px Arial";
    visibleInsights.forEach((insight, index) => {
      wrapCanvasText(ctx, `• ${String(insight)}`, 148, y + 100 + index * 36, width - 310, 28, 1);
    });
    y += notesBoxHeight + 58;
  }

  ctx.strokeStyle = "#E5E7EB";
  ctx.beginPath();
  ctx.moveTo(120, height - 118);
  ctx.lineTo(width - 120, height - 118);
  ctx.stroke();

  ctx.fillStyle = "#737373";
  ctx.font = "700 18px Arial";
  ctx.fillText("GENERATED BY ORDENELI", 120, height - 78);
  ctx.fillText("ORDENELI.COM", width - 275, height - 78);

  canvas.toBlob((blob) => {
    if (!blob) {
      toast.error("Could not export JPEG.");
      return;
    }
    downloadBlob(blob, `${slugify(normalized.title)}.jpg`);
    toast.success("JPEG exported.");
  }, "image/jpeg", 0.92);
}

function GeneratedDiagramCard({ diagram, onDelete, onEdit }) {
  const normalized = normalizeGeneratedDiagram(diagram);
  if (!normalized) return null;

  const nodes = normalized.nodes.length > 0 ? normalized.nodes : [];
  const type = normalized.type;

  const handleExportPdf = () => exportDiagramPdf(diagram);
  const handleExportJpeg = () => exportDiagramJpeg(diagram);
  const handleCopyText = () => copyDiagramText(diagram);
  const handleEdit = () => onEdit && onEdit(diagram);

  return (
    <div className="border border-[#E5E7EB] bg-white overflow-hidden">
      <div className="p-5 border-b border-[#E5E7EB] flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-2xl font-black">{normalized.title}</h3>
            <MiniPill status="active">{diagramTypeLabel(type)}</MiniPill>
          </div>
          {normalized.summary && (
            <p className="mt-2 text-sm text-[#525252] max-w-3xl leading-relaxed">{normalized.summary}</p>
          )}
          {normalized.created_at && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
              Generated {new Date(normalized.created_at).toLocaleString()}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {onEdit && normalized.diagram_id && (
            <button
              type="button"
              onClick={handleEdit}
              className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5]"
            >
              Edit
            </button>
          )}

          <button
            type="button"
            onClick={handleExportPdf}
            className="border border-[#002FA7] text-[#002FA7] px-3 py-2 text-xs font-bold hover:bg-[#EFF6FF]"
          >
            Export PDF
          </button>

          <button
            type="button"
            onClick={handleExportJpeg}
            className="border border-[#002FA7] text-[#002FA7] px-3 py-2 text-xs font-bold hover:bg-[#EFF6FF]"
          >
            Export JPEG
          </button>

          <button
            type="button"
            onClick={handleCopyText}
            className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5]"
          >
            Copy text
          </button>

          {onDelete && normalized.diagram_id && (
            <button
              type="button"
              onClick={() => onDelete(normalized.diagram_id)}
              className="border border-[#FECACA] text-[#B91C1C] px-3 py-2 text-xs font-bold hover:bg-[#FEF2F2] inline-flex items-center gap-1.5"
            >
              <Trash size={14} weight="bold" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="p-5">
        {type === "campaign_funnel" ? (
          <div className="space-y-3">
            {nodes.map((node, index) => (
              <div key={node.id || node.label || index} className="flex justify-center">
                <div
                  className={`border p-5 ${nodeStyle(index, type)}`}
                  style={{ width: `${Math.max(54, 100 - index * 10)}%` }}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-70">Stage {index + 1}</p>
                      <h4 className="font-display text-xl font-black mt-1">{node.label || `Stage ${index + 1}`}</h4>
                      <p className="mt-2 text-sm opacity-80 leading-relaxed">{node.description || node.note || ""}</p>
                    </div>
                    {node.metric && <span className="font-display text-3xl font-black">{node.metric}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : type === "customer_journey" ? (
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
            {nodes.map((node, index) => (
              <div key={node.id || node.label || index} className={`relative border p-5 ${nodeStyle(index, type)}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Step {index + 1}</p>
                <h4 className="font-display text-xl font-black mt-2">{node.label || `Step ${index + 1}`}</h4>
                <p className="mt-2 text-sm text-[#525252] leading-relaxed">{node.description || node.note || ""}</p>
                {index < nodes.length - 1 && (
                  <div className="hidden xl:flex absolute top-8 -right-3 text-[#002FA7]">
                    <ArrowRight size={18} weight="bold" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : type === "proposal_visual" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nodes.map((node, index) => (
              <div key={node.id || node.label || index} className={`border p-5 min-h-[150px] ${nodeStyle(index, type)}`}>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-70">Block {index + 1}</p>
                <h4 className="font-display text-2xl font-black mt-2">{node.label || `Block ${index + 1}`}</h4>
                <p className="mt-3 text-sm opacity-80 leading-relaxed">{node.description || node.note || ""}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
            {nodes.slice(0, 4).map((node, index) => (
              <div key={node.id || node.label || index} className="contents">
                <div className={`border p-5 min-h-[150px] ${nodeStyle(index, type)}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Node {index + 1}</p>
                  <h4 className="font-display text-xl font-black mt-2">{node.label || `Node ${index + 1}`}</h4>
                  <p className="mt-2 text-sm text-[#525252] leading-relaxed">{node.description || node.note || ""}</p>
                </div>
                {index < Math.min(nodes.length, 4) - 1 && <Connector />}
              </div>
            ))}
          </div>
        )}

        {normalized.insights.length > 0 && (
          <div className="mt-5 border border-[#E5E7EB] bg-[#F8FAFC] p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">AI notes</p>
            <ul className="mt-3 grid gap-2 text-sm text-[#525252]">
              {normalized.insights.slice(0, 4).map((insight, index) => (
                <li key={index} className="flex gap-2">
                  <SealCheck size={16} weight="duotone" className="text-[#002FA7] shrink-0 mt-0.5" />
                  <span>{String(insight)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function DiagramEditModal({ diagram, open, saving, onClose, onSave }) {
  const normalized = normalizeGeneratedDiagram(diagram);
  const [title, setTitle] = useState("");
  const [diagramType, setDiagramType] = useState("workflow_map");
  const [summary, setSummary] = useState("");
  const [nodes, setNodes] = useState([]);
  const [insightsText, setInsightsText] = useState("");

  useEffect(() => {
    if (!open || !normalized) return;
    setTitle(normalized.title || "Strategy diagram");
    setDiagramType(normalized.type || "workflow_map");
    setSummary(normalized.summary || "");
    setNodes((normalized.nodes || []).map((node, index) => ({
      id: node.id || `n${index + 1}`,
      label: node.label || "",
      description: node.description || node.note || "",
      metric: node.metric || "",
    })));
    setInsightsText((normalized.insights || []).join("\n"));
  }, [open, diagram]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !normalized) return null;

  const updateNode = (index, key, value) => {
    setNodes((prev) => prev.map((node, i) => (i === index ? { ...node, [key]: value } : node)));
  };

  const moveNode = (index, direction) => {
    setNodes((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addNode = () => {
    setNodes((prev) => [...prev, {
      id: `n${prev.length + 1}`,
      label: "New step",
      description: "Describe what happens at this stage.",
      metric: "",
    }]);
  };

  const removeNode = (index) => {
    setNodes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const cleanNodes = nodes
      .map((node, index) => ({
        id: node.id || `n${index + 1}`,
        label: String(node.label || "").trim(),
        description: String(node.description || "").trim(),
        metric: String(node.metric || "").trim(),
      }))
      .filter((node) => node.label !== "");

    if (!title.trim()) {
      toast.error("Diagram title is required.");
      return;
    }
    if (cleanNodes.length < 1) {
      toast.error("Keep at least one diagram node.");
      return;
    }

    onSave(normalized.diagram_id, {
      title: title.trim(),
      diagram_type: diagramType,
      summary: summary.trim(),
      nodes: cleanNodes,
      insights: insightsText.split("\n").map((item) => item.trim()).filter(Boolean),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/65 backdrop-blur-sm flex items-center justify-center px-4 pt-24 pb-24" style={{ zIndex: 40 }} onClick={saving ? undefined : onClose}>
      <div className="relative w-full max-w-5xl max-h-[calc(100vh-12rem)] bg-white border border-[#E5E7EB] shadow-2xl overflow-hidden" style={{ zIndex: 45 }} onClick={(event) => event.stopPropagation()}>
        <div className="px-5 sm:px-6 py-5 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Edit strategy diagram</p>
            <h3 className="font-display text-2xl sm:text-3xl font-black mt-1">Make AI output client-ready.</h3>
            <p className="mt-2 text-sm text-[#525252] max-w-2xl">Update labels, descriptions, notes and node order before exporting as PDF or JPEG.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="border border-[#E5E7EB] px-3 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50">Close</button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(100vh-20rem)]">
          <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4 border-b border-[#E5E7EB]">
            <label className="lg:col-span-2 flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Diagram title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="input-line" maxLength={180} required />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Diagram type</span>
              <select value={diagramType} onChange={(e) => setDiagramType(e.target.value)} className="input-line">
                {DIAGRAM_TYPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <label className="lg:col-span-3 flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Summary</span>
              <textarea value={summary} onChange={(e) => setSummary(e.target.value)} className="input-line min-h-[86px] resize-y" maxLength={300} />
            </label>
          </div>

          <div className="p-5 sm:p-6 border-b border-[#E5E7EB]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#737373]">Diagram nodes</p>
                <h4 className="font-display text-2xl font-black mt-1">Edit the visual steps.</h4>
              </div>
              <button type="button" onClick={addNode} className="btn-secondary justify-center">Add node</button>
            </div>
            <div className="mt-5 grid gap-4">
              {nodes.map((node, index) => (
                <div key={`${node.id}-${index}`} className="border border-[#E5E7EB] bg-[#F8FAFC] p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Node {index + 1}</p>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => moveNode(index, -1)} disabled={index === 0} className="border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-bold disabled:opacity-40">Up</button>
                      <button type="button" onClick={() => moveNode(index, 1)} disabled={index === nodes.length - 1} className="border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-bold disabled:opacity-40">Down</button>
                      <button type="button" onClick={() => removeNode(index)} disabled={nodes.length <= 1} className="border border-[#FECACA] text-[#B91C1C] bg-white px-3 py-1.5 text-xs font-bold disabled:opacity-40">Remove</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-semibold">Label</span>
                      <input value={node.label} onChange={(e) => updateNode(index, "label", e.target.value)} className="input-line bg-white" maxLength={80} required />
                    </label>
                    <label className="md:col-span-2 flex flex-col gap-1.5">
                      <span className="text-xs font-semibold">Description</span>
                      <input value={node.description} onChange={(e) => updateNode(index, "description", e.target.value)} className="input-line bg-white" maxLength={280} />
                    </label>
                    <label className="md:col-span-3 flex flex-col gap-1.5">
                      <span className="text-xs font-semibold">Optional metric / badge</span>
                      <input value={node.metric} onChange={(e) => updateNode(index, "metric", e.target.value)} className="input-line bg-white" maxLength={40} placeholder="Example: 4 workflows, 2 approvals, 12 outputs" />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5 sm:p-6 border-b border-[#E5E7EB]">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">AI notes</span>
              <textarea value={insightsText} onChange={(e) => setInsightsText(e.target.value)} className="input-line min-h-[110px] resize-y" placeholder="One note per line" />
            </label>
          </div>

          <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-end gap-3 bg-[#FAFAFA]">
            <button type="button" onClick={onClose} disabled={saving} className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-white disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary justify-center disabled:opacity-50">{saving ? "Saving…" : "Save diagram"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GenerateDiagramPanel({
  projects,
  savedDiagrams,
  form,
  setForm,
  generating,
  onGenerate,
  onDelete,
  onEdit,
}) {
  const activeProjects = projects.filter((project) => !isArchivedProject(project));

  return (
    <div className="border border-[#E5E7EB] bg-white p-6 md:p-8">
      <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">AI diagram generator</p>
          <h2 className="font-display text-3xl font-black mt-1">Generate a strategy diagram from project data.</h2>
          <p className="mt-2 text-sm text-[#525252] max-w-3xl leading-relaxed">
            Select a project and visual type. Ordeneli will use the project, workflow, approval, task and history context to create an editable strategy-style diagram.
          </p>
        </div>

        <form onSubmit={onGenerate} className="w-full xl:max-w-xl grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Project</span>
            <select
              value={form.project_id}
              onChange={(e) => setForm((prev) => ({ ...prev, project_id: e.target.value }))}
              className="input-line"
              required
            >
              <option value="">Choose project</option>
              {activeProjects.map((project) => (
                <option key={project.project_id} value={project.project_id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Diagram type</span>
            <select
              value={form.diagram_type}
              onChange={(e) => setForm((prev) => ({ ...prev, diagram_type: e.target.value }))}
              className="input-line"
            >
              {DIAGRAM_TYPE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>

          <label className="md:col-span-2 flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Optional instruction</span>
            <input
              value={form.prompt}
              onChange={(e) => setForm((prev) => ({ ...prev, prompt: e.target.value }))}
              placeholder="Example: make this suitable for a client proposal"
              className="input-line"
              maxLength={300}
            />
          </label>

          <button
            type="submit"
            disabled={generating || !form.project_id}
            className="btn-primary md:col-span-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} weight="bold" />
            {generating ? "Generating diagram…" : "Generate strategy diagram"}
          </button>
        </form>
      </div>

      <div className="mt-7 border-t border-[#E5E7EB] pt-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#737373]">Saved AI diagrams</p>
            <h3 className="font-display text-2xl font-black mt-1">{savedDiagrams.length} saved visual{savedDiagrams.length === 1 ? "" : "s"}</h3>
          </div>
        </div>

        {savedDiagrams.length === 0 ? (
          <div className="border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-6 text-sm text-[#525252]">
            No AI strategy diagrams yet. Generate one from a project to save it here.
          </div>
        ) : (
          <div className="grid gap-5">
            {savedDiagrams.map((diagram) => (
              <GeneratedDiagramCard
                key={diagram.diagram_id || diagram.id || diagram.title}
                diagram={diagram}
                onDelete={onDelete}
                onEdit={onEdit}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function StrategyMaps() {
  const { user, usage } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("map");
  const [savedDiagrams, setSavedDiagrams] = useState([]);
  const [generatingDiagram, setGeneratingDiagram] = useState(false);
  const [editingDiagram, setEditingDiagram] = useState(null);
  const [savingDiagram, setSavingDiagram] = useState(false);
  const [diagramForm, setDiagramForm] = useState({
    project_id: "",
    diagram_type: "campaign_funnel",
    prompt: "",
  });

  const plan = normalizePlan(usage?.plan || user?.plan);
  const locked = !isProLike(plan);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [projectRes, workflowRes, approvalRes, taskRes, diagramRes] = await Promise.allSettled([
        axios.get(`${API}/projects`),
        axios.get(`${API}/workflows`),
        axios.get(`${API}/approvals`),
        axios.get(`${API}/action-tasks`),
        axios.get(`${API}/strategy-diagrams`),
      ]);
      if (projectRes.status === "fulfilled") setProjects(readItems(projectRes.value.data));
      if (workflowRes.status === "fulfilled") setWorkflows(readItems(workflowRes.value.data));
      if (approvalRes.status === "fulfilled") setApprovals(readItems(approvalRes.value.data));
      if (taskRes.status === "fulfilled") setTasks(readItems(taskRes.value.data));
      if (diagramRes.status === "fulfilled") setSavedDiagrams(readItems(diagramRes.value.data));
      const failed = [projectRes, workflowRes, approvalRes, taskRes].filter((result) => result.status === "rejected");
      if (failed.length >= 4) setError("Could not load strategy data.");
    } catch {
      setError("Could not load strategy data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!locked) load();
  }, [locked]);

  const totals = useMemo(() => {
    const activeProjects = projects.filter((item) => !isArchivedProject(item)).length;
    const archivedProjects = projects.filter(isArchivedProject).length;
    const activeWorkflows = workflows.filter((item) => workflowStatus(item) === "active").length;
    const pendingApprovals = approvals.filter((item) => safeStatus(item?.status) === "pending").length;
    const readyTasks = tasks.filter((item) => safeStatus(item?.status) === "ready").length;
    const scheduledTasks = tasks.filter((item) => safeStatus(item?.status) === "scheduled").length;
    const publishedTasks = tasks.filter((item) => safeStatus(item?.status) === "published").length;
    const failedTasks = tasks.filter((item) => safeStatus(item?.status) === "failed").length;
    return { activeProjects, archivedProjects, activeWorkflows, pendingApprovals, readyTasks, scheduledTasks, publishedTasks, failedTasks };
  }, [projects, workflows, approvals, tasks]);

  const projectCards = useMemo(() => {
    const activeFirst = [...projects].sort((a, b) => {
      const aArchived = isArchivedProject(a) ? 1 : 0;
      const bArchived = isArchivedProject(b) ? 1 : 0;
      return aArchived - bArchived;
    });

    return activeFirst.slice(0, 6).map((project) => {
      const pid = String(project?.project_id || "").trim();
      const pname = String(project?.name || "").trim().toLowerCase();
      const matchesProject = (item) => {
        const itemPid = itemProjectId(item);
        const itemPname = itemProjectName(item).toLowerCase();
        if (pid && itemPid && pid === itemPid) return true;
        if (pname && itemPname && pname === itemPname) return true;
        return false;
      };
      return { project, workflows: workflows.filter(matchesProject), approvals: approvals.filter(matchesProject), tasks: tasks.filter(matchesProject) };
    });
  }, [projects, workflows, approvals, tasks]);

  const generateStrategyDiagram = async (event) => {
    event.preventDefault();

    if (!diagramForm.project_id) {
      setError("Choose a project first.");
      return;
    }

    setGeneratingDiagram(true);
    setError("");

    try {
      const { data } = await axios.post(`${API}/strategy-diagrams/generate`, {
        project_id: diagramForm.project_id,
        diagram_type: diagramForm.diagram_type,
        prompt: diagramForm.prompt,
      });

      const diagram = data?.diagram || data;
      if (diagram) {
        setSavedDiagrams((prev) => [diagram, ...prev]);
      }

      setDiagramForm((prev) => ({ ...prev, prompt: "" }));
    } catch (err) {
      const message = err?.response?.data?.detail || err?.response?.data?.error || "Could not generate strategy diagram.";
      setError(typeof message === "string" ? message : "Could not generate strategy diagram.");
    } finally {
      setGeneratingDiagram(false);
    }
  };

  const deleteStrategyDiagram = async (diagramId) => {
    if (!diagramId) return;

    try {
      await axios.delete(`${API}/strategy-diagrams/${encodeURIComponent(diagramId)}`);
      setSavedDiagrams((prev) => prev.filter((item) => item.diagram_id !== diagramId));
    } catch (err) {
      const message = err?.response?.data?.detail || err?.response?.data?.error || "Could not delete strategy diagram.";
      setError(typeof message === "string" ? message : "Could not delete strategy diagram.");
    }
  };

  const updateStrategyDiagram = async (diagramId, payload) => {
    if (!diagramId) return;

    setSavingDiagram(true);
    setError("");

    try {
      const { data } = await axios.patch(`${API}/strategy-diagrams/${encodeURIComponent(diagramId)}`, payload);
      const updatedDiagram = data?.diagram || data;

      if (updatedDiagram) {
        setSavedDiagrams((prev) => prev.map((item) => {
          const existingId = item.diagram_id || item.id;
          return existingId === diagramId ? updatedDiagram : item;
        }));
      }

      setEditingDiagram(null);
      toast.success("Strategy diagram saved.");
    } catch (err) {
      const message = err?.response?.data?.detail || err?.response?.data?.error || "Could not save strategy diagram.";
      setError(typeof message === "string" ? message : "Could not save strategy diagram.");
      toast.error(typeof message === "string" ? message : "Could not save strategy diagram.");
    } finally {
      setSavingDiagram(false);
    }
  };

  if (locked) {
    return (
      <div>
        <section className="border-b border-[#E5E7EB]">
          <div className="container-wide py-10 md:py-14">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">Strategy maps</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Visual strategy is a Pro workspace feature.</h1>
            <p className="mt-3 text-[#525252] max-w-2xl">Upgrade to Pro to turn workflow activity, project usage, approvals and publishing into client-friendly visual maps.</p>
            <Link to="/pricing" className="btn-primary inline-flex mt-6">Upgrade to Pro</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        @keyframes ordeneliMapIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

      `}</style>

      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">Strategy maps</p>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Visual strategy for your workspace.</h1>
              <p className="mt-3 text-[#525252] max-w-3xl">Make usage feel visual, not just numerical. Turn project activity, workflow health, approvals and publishing into clear diagram styles you can later use in reports and exports.</p>
            </div>
            <button type="button" onClick={load} disabled={loading} className="btn-secondary inline-flex justify-center">
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} weight="bold" />}
              Refresh map
            </button>
          </div>
        </div>
      </section>

      <section className="container-wide py-10 md:py-14 space-y-8">
        {error && <div className="border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#B91C1C] flex items-start gap-2"><WarningCircle size={18} weight="bold" />{error}</div>}

        <GenerateDiagramPanel
          projects={projects}
          savedDiagrams={savedDiagrams}
          form={diagramForm}
          setForm={setDiagramForm}
          generating={generatingDiagram}
          onGenerate={generateStrategyDiagram}
          onDelete={deleteStrategyDiagram}
          onEdit={setEditingDiagram}
        />

        <DiagramEditModal
          open={!!editingDiagram}
          diagram={editingDiagram}
          saving={savingDiagram}
          onClose={() => setEditingDiagram(null)}
          onSave={updateStrategyDiagram}
        />

        <div className="border border-[#E5E7EB] bg-[#FAFAFA] p-6 md:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Visual modes</p>
              <h2 className="font-display text-3xl font-black mt-1">Not every insight needs the same diagram.</h2>
              <p className="mt-2 text-sm text-[#525252] max-w-3xl">Switch between different visual styles. This is the direction for making Strategy Maps feel richer, more premium and closer to proposal-quality outputs.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <VisualModeButton active={mode === "map"} onClick={() => setMode("map")}>Flow map</VisualModeButton>
              <VisualModeButton active={mode === "funnel"} onClick={() => setMode("funnel")}>Funnel</VisualModeButton>
              <VisualModeButton active={mode === "journey"} onClick={() => setMode("journey")}>Journey</VisualModeButton>
              <VisualModeButton active={mode === "snapshot"} onClick={() => setMode("snapshot")}>Snapshot</VisualModeButton>
            </div>
          </div>
        </div>

        {mode === "map" && <WorkspaceMap totals={totals} />}
        {mode === "funnel" && <FunnelCanvas totals={totals} />}
        {mode === "journey" && <JourneyCanvas totals={totals} />}
        {mode === "snapshot" && <SnapshotCanvas totals={totals} usage={usage || {}} />}

        <div>
          <div className="mb-5 flex flex-col md:flex-row md:items-end justify-between gap-3">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Project maps</p>
              <h2 className="font-display text-3xl font-black mt-1">Activity by project.</h2>
            </div>
            <Link to="/settings?tab=plan" className="btn-secondary inline-flex justify-center">Manage projects</Link>
          </div>

          {loading ? (
            <div className="grid gap-4">{[1, 2].map((item) => <div key={item} className="h-52 bg-[#F5F5F5] animate-pulse border border-[#E5E7EB]" />)}</div>
          ) : projectCards.length === 0 ? (
            <EmptyVisual title="No projects to map yet." text="Create your first project, then connect workflows, approvals and action tasks to start building a visual strategy map." actionText="Create a project" to="/settings?tab=plan" />
          ) : (
            <ProjectCards projectCards={projectCards} />
          )}
        </div>

        <FutureDiagramPanel />
      </section>
    </div>
  );
}
